import type { VehicleStatusHistoryItem } from '@/services/inventory'
import { INVENTORY_LIFECYCLE_EVENT_TYPE } from '@jazel-auto/ise-contracts/dist/inventory.js'
import { MANUAL_VEHICLE_WEBSITE_VISIBILITY_REASON_OPTIONS } from '@/utils/manualVehicleWebsiteVisibility'

type UnknownRecord = Record<string, unknown>

function toDisplayDateParts(iso: string): { dateTop: string; timeBottom: string } {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return { dateTop: iso, timeBottom: '' }

  // Date: "Feb. 13, 2026"
  const parts = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).formatToParts(d)
  const month = (parts.find((p) => p.type === 'month')?.value || '').trim()
  const day = (parts.find((p) => p.type === 'day')?.value || '').trim()
  const year = (parts.find((p) => p.type === 'year')?.value || '').trim()
  const monthWithDot = month && !month.endsWith('.') ? `${month}.` : month
  const dateTop = monthWithDot && day && year ? `${monthWithDot} ${day}, ${year}` : d.toLocaleDateString()

  // Time: "1:26 AM EST"
  const timeBottom = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(d)

  return { dateTop, timeBottom }
}

function getObj(x: unknown): UnknownRecord | null {
  return x && typeof x === 'object' && !Array.isArray(x) ? (x as UnknownRecord) : null
}

function getNestedString(obj: unknown, path: string[]): string | undefined {
  let cur: unknown = obj
  for (const key of path) {
    const o = getObj(cur)
    if (!o) return undefined
    cur = o[key]
  }
  return typeof cur === 'string' && cur.trim() ? cur.trim() : undefined
}

function normalizeEventType(eventType: unknown): string {
  return String(eventType || '')
    .trim()
    .toUpperCase()
}

export function formatVehicleHistoryReason(reasonRaw: unknown, _opts?: { eventType?: string }): string {
  const reason = String(reasonRaw || '').trim()
  if (!reason) return ''

  const normalized = reason.toLowerCase()

  const labels: Record<string, string> = {
    manual_published: 'Vehicle was published from manual inventory.',
    manual_created: 'Vehicle was created manually.',
    manual_updated: 'Vehicle was updated manually.',
    manual_removed_published: 'Vehicle was archived from manual inventory.',
    removed_from_feed: 'Vehicle was removed because it no longer appears in the feed.',
    hidden_by_rules: 'Vehicle was hidden by publish rules.',
    visibility_rules: 'Vehicle visibility changed because of publish rules.',
    blocked_by_publish_rules: 'Vehicle was excluded by publish rules.',
    feed_state_archived: 'Vehicle was archived based on feed state.',
    manual_state_archived: 'Vehicle was archived manually.',
    removed_by_user: 'Vehicle was removed by a user.',
    restored_by_user: 'Vehicle was restored by a user.',
  }

  if (labels[normalized]) return labels[normalized]

  return reason
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

export function formatVehicleHistoryReasonLabel(reasonRaw: unknown): string {
  const sentence = formatVehicleHistoryReason(reasonRaw)
  const labels: Record<string, string> = {
    manual_published: 'Manual publish',
    manual_created: 'Manual create',
    manual_updated: 'Manual update',
    manual_removed_published: 'Archived manually',
    removed_from_feed: 'Removed from feed',
    hidden_by_rules: 'Hidden by rules',
    visibility_rules: 'Visibility rules',
    blocked_by_publish_rules: 'Blocked by publish rules',
    feed_state_archived: 'Archived by feed state',
    manual_state_archived: 'Archived manually',
    removed_by_user: 'Removed by user',
    restored_by_user: 'Restored by user',
  }
  const normalized = String(reasonRaw || '').trim().toLowerCase()
  return labels[normalized] || sentence
}

function operationLabelFromEvent(eventType: string): string {
  switch (eventType) {
    case INVENTORY_LIFECYCLE_EVENT_TYPE.CREATED:
      return 'Created'
    case 'CHANGED':
    case INVENTORY_LIFECYCLE_EVENT_TYPE.UPDATED:
      return 'Updated'
    case INVENTORY_LIFECYCLE_EVENT_TYPE.STATE_CHANGED:
      return 'Inventory state changed'
    case INVENTORY_LIFECYCLE_EVENT_TYPE.USAGE_TYPE_CHANGED:
      return 'Usage type changed'
    case INVENTORY_LIFECYCLE_EVENT_TYPE.WEBSITE_VISIBILITY_CHANGED:
      return 'Website visibility changed'
    case INVENTORY_LIFECYCLE_EVENT_TYPE.ARCHIVED:
      return 'Archived'
    case INVENTORY_LIFECYCLE_EVENT_TYPE.RESTORED:
      return 'Restored'
    case INVENTORY_LIFECYCLE_EVENT_TYPE.FEED_RECLAIMED_MANUAL_RESTORE:
      return 'Reclaimed by feed'
    case INVENTORY_LIFECYCLE_EVENT_TYPE.PURGED:
      return 'Purged'
    default:
      return eventType || 'Unknown'
  }
}

function operationShortFromEvent(eventType: string): string {
  switch (eventType) {
    case INVENTORY_LIFECYCLE_EVENT_TYPE.CREATED:
      return 'Created'
    case INVENTORY_LIFECYCLE_EVENT_TYPE.UPDATED:
      return 'Updated'
    case INVENTORY_LIFECYCLE_EVENT_TYPE.STATE_CHANGED:
      return 'State'
    case INVENTORY_LIFECYCLE_EVENT_TYPE.USAGE_TYPE_CHANGED:
      return 'Usage'
    case INVENTORY_LIFECYCLE_EVENT_TYPE.WEBSITE_VISIBILITY_CHANGED:
      return 'Visibility'
    case INVENTORY_LIFECYCLE_EVENT_TYPE.ARCHIVED:
      return 'Archived'
    case INVENTORY_LIFECYCLE_EVENT_TYPE.RESTORED:
      return 'Restored'
    case INVENTORY_LIFECYCLE_EVENT_TYPE.FEED_RECLAIMED_MANUAL_RESTORE:
      return 'Reclaimed'
    case INVENTORY_LIFECYCLE_EVENT_TYPE.PURGED:
      return 'Purged'
    default:
      return eventType || 'Unknown'
  }
}

export function formatVehicleHistoryActor(row: VehicleStatusHistoryItem): string {
  const createdBy = String(row.createdBy || '').trim()
  if (createdBy) return createdBy

  const meta = row.metadata

  const username =
    getNestedString(meta, ['removedBy', 'username']) ||
    getNestedString(meta, ['deletedBy', 'username']) ||
    getNestedString(meta, ['changedBy', 'username']) ||
    getNestedString(meta, ['updatedBy', 'username']) ||
    getNestedString(meta, ['deleteRequestedBy', 'username']) ||
    getNestedString(meta, ['requestedBy', 'username']) ||
    getNestedString(meta, ['requestedByUser', 'username']) ||
    getNestedString(meta, ['requestedByUsername'])

  if (username) return username

  const updatedByUserId =
    getNestedString(meta, ['updatedByUserId']) ||
    getNestedString(meta, ['savedByUserId']) ||
    getNestedString(meta, ['userId'])
  if (updatedByUserId) return updatedByUserId

  const sourceFeed = String(row.sourceFeed || '').trim()
  if (sourceFeed) {
    if (sourceFeed.toLowerCase() === 'inventory_membership_rules') return 'Publish rules'
    if (sourceFeed.toLowerCase() === 'manual_add' || sourceFeed.toLowerCase() === 'custom_feed') return 'Manual'
    return sourceFeed
  }

  const source = String(row.source || '').trim()
  if (source) return source

  return 'System'
}

function summarizeChangesFromMetadata(meta: unknown): string[] {
  const m = getObj(meta)
  if (!m) return []

  if (Array.isArray(m.changedFields)) {
    const fields = m.changedFields
      .map((x) => {
        const entry = getObj(x)
        if (entry) return String(entry.field || '').trim()
        return String(x || '').trim()
      })
      .filter(Boolean)
    if (fields.length > 0) return [`Fields: ${fields.join(', ')}`]
  }

  const diff = getObj(m.diff)
  if (diff) {
    const lines: string[] = []
    for (const [k, v] of Object.entries(diff)) {
      const vv = getObj(v)
      if (vv && ('before' in vv || 'after' in vv)) {
        const before = String((vv as any).before ?? '—')
        const after = String((vv as any).after ?? '—')
        // Unchanged pairs are not listed (R17.1).
        if (before !== after) lines.push(`${k}: ${before} → ${after}`)
        continue
      }
      if (Array.isArray(v) && v.length === 2) {
        const before = String(v[0] ?? '—')
        const after = String(v[1] ?? '—')
        if (before !== after) lines.push(`${k}: ${before} → ${after}`)
        continue
      }
      lines.push(`${k}: updated`)
    }
    if (lines.length > 0) return lines
  }

  const changesObj = getObj(m.changes) || getObj(m.changed) || getObj(m.patch)
  if (changesObj) {
    const keys = Object.keys(changesObj).filter(Boolean)
    if (keys.length > 0) return [`Fields: ${keys.join(', ')}`]
  }

  return []
}

function formatUsd(value: unknown): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return String(value ?? '—')
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(
      value,
    )
  } catch {
    return String(value)
  }
}

function valueToString(key: string, value: unknown): string {
  if (value == null) return '—'
  if (key === 'price') return formatUsd(value)
  if (Array.isArray(value)) return value.map((v) => String(v ?? '')).filter(Boolean).join(', ') || '—'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

function hasOwn(obj: UnknownRecord, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(obj, key)
}

function changedFieldRowsFromMetadata(meta: unknown): Array<{ field: string; before: string; after: string }> {
  const m = getObj(meta)
  if (!m) return []

  if (Array.isArray(m.changedFields)) {
    const rows = m.changedFields
      .map((entryRaw) => {
        const entry = getObj(entryRaw)
        const field = String(entry?.field || '').trim()
        if (!entry || !field) return null

        const hasPreviousValue = hasOwn(entry, 'previousValue')
        const hasNewValue = hasOwn(entry, 'newValue')
        if (!hasPreviousValue && !hasNewValue) return null

        return {
          field,
          before: valueToString(field, hasPreviousValue ? entry.previousValue : null),
          after: valueToString(field, hasNewValue ? entry.newValue : null),
        }
      })
      .filter((row): row is { field: string; before: string; after: string } => Boolean(row))

    if (rows.length > 0) return rows
  }

  const diff = getObj(m.diff)
  if (diff) {
    const rows: Array<{ field: string; before: string; after: string }> = []
    for (const [field, value] of Object.entries(diff)) {
      const valueObj = getObj(value)
      if (valueObj && (hasOwn(valueObj, 'before') || hasOwn(valueObj, 'after'))) {
        rows.push({
          field,
          before: valueToString(field, hasOwn(valueObj, 'before') ? valueObj.before : null),
          after: valueToString(field, hasOwn(valueObj, 'after') ? valueObj.after : null),
        })
        continue
      }

      if (Array.isArray(value) && value.length === 2) {
        rows.push({
          field,
          before: valueToString(field, value[0]),
          after: valueToString(field, value[1]),
        })
      }
    }
    if (rows.length > 0) return rows
  }

  return []
}

function diffChangedFields(params: {
  current: VehicleStatusHistoryItem
  baseline: VehicleStatusHistoryItem
  maxLines?: number
}): Array<{ field: string; before: string; after: string }> {
  const { current, baseline } = params
  const maxLines = typeof params.maxLines === 'number' && params.maxLines > 0 ? params.maxLines : 6

  const keys: string[] = ['year', 'make', 'model', 'trim', 'stockNumber', 'condition', 'price', 'segments']

  const rows: Array<{ field: string; before: string; after: string }> = []
  for (const key of keys) {
    const curVal = (current as any)[key]
    const baseVal = (baseline as any)[key]

    const same =
      Array.isArray(curVal) && Array.isArray(baseVal)
        ? curVal.map(String).join('|') === baseVal.map(String).join('|')
        : curVal === baseVal
    if (same) continue
    if (curVal == null && baseVal == null) continue

    rows.push({ field: key, before: valueToString(key, baseVal), after: valueToString(key, curVal) })
    if (rows.length >= maxLines) break
  }

  return rows
}

// A history pair is listed only when its displayed values differ (R17.1). Both absent -> nothing,
// as before; an absent side shows "—" and counts as a change.
function transitionLine(label: string, previous: unknown, next: unknown): string | null {
  if (!previous && !next) return null
  const before = String(previous ?? '—')
  const after = String(next ?? '—')
  return before === after ? null : `${label}: ${before} → ${after}`
}

function normalizeVisibilityReason(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed ? trimmed : undefined
}

// Human label for a recorded hide reason. Only the event's own recorded code is ever passed in; codes
// that are not hide reasons (descriptors, hidden_by_rules, unknown codes) have no label.
function visibilityReasonLabel(code: unknown): string | undefined {
  const reason = normalizeVisibilityReason(code)
  if (!reason) return undefined
  if (reason === 'inventory_state_unknown') return 'Inventory state is Unknown.'
  if (reason === 'inventory_state_sold') return 'Inventory state is Sold.'
  const preset = MANUAL_VEHICLE_WEBSITE_VISIBILITY_REASON_OPTIONS.find((option) => option.value === reason)
  if (preset) return preset.label
  const lowered = reason.toLowerCase()
  if (lowered === 'srp_missing') return 'Vehicle data unavailable (SRP missing)'
  if (lowered === 'opensearch_upsert_failed') return 'Website indexing failed'
  return undefined
}

// Accordion title summary: up to `max` changed field names, then "+N more" (R17.3).
export function summarizeHistoryFieldNames(changes: Array<{ field: string }>, max = 3): string {
  const names = changes.map((c) => c.field)
  return names.length <= max ? names.join(', ') : `${names.slice(0, max).join(', ')} +${names.length - max} more`
}

export type VehicleHistoryDisplay = {
  dateTop: string
  timeBottom: string
  operation: string
  operationShort: string
  by: string
  details?: string
  changed?: string[]
  changes?: Array<{ field: string; before: string; after: string }>
}

export function formatVehicleHistoryRow(
  row: VehicleStatusHistoryItem,
  opts?: { baseline?: VehicleStatusHistoryItem | null },
): VehicleHistoryDisplay {
  const eventType = normalizeEventType(row.eventType)
  const isInitialVisibility = eventType === INVENTORY_LIFECYCLE_EVENT_TYPE.WEBSITE_VISIBILITY_CHANGED && row.metadata?.visibilityDecisionKind === 'INITIAL'
  // Reason-only update (R3.3a): a recorded TRANSITION that stays HIDDEN while the recorded hide reason
  // changed. Both reasons come from this event's own fields.
  const isReasonOnly = eventType === INVENTORY_LIFECYCLE_EVENT_TYPE.WEBSITE_VISIBILITY_CHANGED
    && row.metadata?.visibilityDecisionKind === 'TRANSITION'
    && row.previousWebsiteVisibility === 'HIDDEN' && row.nextWebsiteVisibility === 'HIDDEN'
    && normalizeVisibilityReason(row.reason) !== normalizeVisibilityReason(row.metadata?.previousVisibilityReason)
  const operation = isReasonOnly ? 'Hide reason changed' : isInitialVisibility ? 'Initial website visibility' : operationLabelFromEvent(eventType)
  const operationShort = isReasonOnly ? 'Hide reason changed' : operationShortFromEvent(eventType)
  const by = formatVehicleHistoryActor(row)
  const { dateTop, timeBottom } = toDisplayDateParts(row.createdAt || row.timestamp || '')
  const previousMode = getNestedString(row.metadata, ['previousWebsiteVisibilityMode'])
  const nextMode = getNestedString(row.metadata, ['nextWebsiteVisibilityMode'])
  const recordedPolicyState = row.metadata?.policyInventoryState ?? row.metadata?.inventoryState
  const policyLabels: Record<string, string> = {
    UNKNOWN: 'Unknown', SOLD: 'Sold', READY_FOR_SALE: 'Ready for Sale',
    READY_COURTESY: 'Ready Courtesy', IN_SERVICE: 'In Service',
    ON_ORDER: 'On Order', IN_PRODUCTION: 'In Production', IN_TRANSIT: 'In Transit', RESERVED: 'Reserved',
  }
  const policyHidden = recordedPolicyState === 'UNKNOWN' || recordedPolicyState === 'SOLD'
  const policyCause = eventType === INVENTORY_LIFECYCLE_EVENT_TYPE.WEBSITE_VISIBILITY_CHANGED
    && row.metadata?.cause === 'inventory_state_policy' && row.metadata?.websiteVisibilityMode === 'AUTO'
    && typeof recordedPolicyState === 'string' && policyLabels[recordedPolicyState]
    && (policyHidden ? row.nextWebsiteVisibility === 'HIDDEN' : row.nextWebsiteVisibility === 'VISIBLE')
    ? `Inventory state is ${policyLabels[recordedPolicyState]}.` : undefined

  const visibilityLine = transitionLine('Website Visibility', row.previousWebsiteVisibility, row.nextWebsiteVisibility)
  const hiddenReasonLabel = !policyCause && row.nextWebsiteVisibility === 'HIDDEN' ? visibilityReasonLabel(row.reason) : undefined
  const hideReasonLine = isReasonOnly
    ? `Hide reason: ${visibilityReasonLabel(row.metadata?.previousVisibilityReason) ?? '—'} → ${visibilityReasonLabel(row.reason) ?? '—'}`
    : null
  const lifecycleChanges = [
    transitionLine('Inventory State', row.previousInventoryState, row.nextInventoryState),
    transitionLine('Usage Type', row.previousInventoryUsageType, row.nextInventoryUsageType),
    row.previousWebsiteVisibility || row.nextWebsiteVisibility
      ? isReasonOnly
        ? hideReasonLine
        : isInitialVisibility
        ? `Initial website visibility: ${row.nextWebsiteVisibility === 'HIDDEN' ? 'Hidden' : 'Visible'}${policyCause ? ` — ${policyCause}` : row.reason === 'inventory_state_unknown' ? ' — Inventory state is Unknown.' : row.reason === 'inventory_state_sold' ? ' — Inventory state is Sold.' : row.reason ? ` — ${row.reason}` : '.'}`
        : visibilityLine && `${visibilityLine}${policyCause ? ` — ${policyCause}` : hiddenReasonLabel ? ` — ${hiddenReasonLabel}` : ''}`
      : null,
    transitionLine('Visibility Mode', previousMode, nextMode),
  ].filter((line): line is string => Boolean(line))

  const details = lifecycleChanges.length === 1 ? lifecycleChanges[0] : undefined

  const metadataRows = changedFieldRowsFromMetadata(row.metadata)
  // Only rows whose displayed value changed are listed (R17.1).
  const metadataChanges = metadataRows.filter((c) => c.before !== c.after)
  // True when the event lists values at all, even if none of them changed (R17.2).
  const listedValues =
    Boolean(row.previousInventoryState || row.nextInventoryState) ||
    Boolean(row.previousInventoryUsageType || row.nextInventoryUsageType) ||
    Boolean(row.previousWebsiteVisibility || row.nextWebsiteVisibility) ||
    Boolean(previousMode || nextMode) ||
    metadataRows.length > 0
  const changes =
    metadataChanges.length > 0
      ? metadataChanges
      : eventType === INVENTORY_LIFECYCLE_EVENT_TYPE.UPDATED && opts?.baseline
        ? (() => {
          const diffRows = diffChangedFields({ current: row, baseline: opts.baseline })
          return diffRows.length > 0 ? diffRows : undefined
          })()
        : undefined

  const changed = (() => {
    if (lifecycleChanges.length > 0) return lifecycleChanges
    if (changes?.length) {
      return changes.map((c) => `${c.field}: ${c.before} → ${c.after}`)
    }

    // Values were listed but none changed: say so instead of listing unchanged values (R17.2).
    if (listedValues) return ['No changes']

    const lines = summarizeChangesFromMetadata(row.metadata)
    if (lines.length > 0) return lines

    const reason = formatVehicleHistoryReason(row.reason, { eventType })
    if (reason) return [reason]

    return eventType === INVENTORY_LIFECYCLE_EVENT_TYPE.UPDATED ? ['(details unavailable)'] : undefined
  })()

  return {
    dateTop,
    timeBottom,
    operation,
    operationShort,
    by,
    details,
    changed,
    changes,
  }
}

export function formatVehicleHistoryTimeline(
  items: VehicleStatusHistoryItem[],
  opts?: { sort?: 'asc' | 'desc' },
): Array<{
  row: VehicleStatusHistoryItem
  display: VehicleHistoryDisplay
}> {
  const sort = opts?.sort === 'asc' || opts?.sort === 'desc' ? opts.sort : 'desc'

  // Baseline must represent the adjacent “previous snapshot”.
  // - desc (newest-first): baseline is next older item (idx+1)
  // - asc (oldest-first): baseline is previous older item (idx-1)
  return items.map((row, idx) => {
    const baseline =
      sort === 'asc' ? (idx - 1 >= 0 ? items[idx - 1] : null) : idx + 1 < items.length ? items[idx + 1] : null
    return { row, display: formatVehicleHistoryRow(row, { baseline }) }
  })
}
